// The back office frame: Za'atar header with numbered stages, KPI strip, then the page.
import React, { useState } from "react";
import { api } from "../../lib/api.js";
import { usePoll } from "../../lib/usePoll.js";
import { navigate } from "../../lib/router.js";
import { signOut, devBypass } from "../../lib/auth.js";
import wordmark from "../../assets/lipari-wordmark-macadamia.svg";
import { StatStrip } from "./ui.jsx";
import InvitationsPage from "./InvitationsPage.jsx";
import EventPage from "./EventPage.jsx";
import AccessPage from "./AccessPage.jsx";
import Agent from "../Agent.jsx";
import HumanPage from "./HumanPage.jsx";
import MonitoringPage from "./MonitoringPage.jsx";
import RecordPage from "./RecordPage.jsx";

// Flow B's stage order. Tier bands and the show floor arrive with the January work.
const STAGES = [
  { n: 1, label: "Invitations", path: "/admin" },
  { n: 2, label: "Needs a human", path: "/admin/human" },
  { n: 3, label: "Event", path: "/admin/event" },
  { n: 4, label: "Monitoring", path: "/admin/monitoring" },
  { n: 5, label: "Codes", path: "/admin/codes" },
  { n: 6, label: "Access", path: "/admin/access" },
  { n: 7, label: "Show floor", path: "/admin/floor", soon: "January" },
];

export default function AdminShell({ path, who, isAdmin, DevSwitch }) {
  const [data, setData] = useState({ counts: {}, invitations: [] });
  const [sum, setSum] = useState(null);
  const [sumFailed, setSumFailed] = useState(false);
  const stage = STAGES.find(s => s.path !== "/admin" && path.startsWith(s.path)) ?? STAGES[0];
  // An invitation's record lives under Invitations (lit in the nav) but doesn't need the whole list.
  const recordId = path.match(/^\/admin\/record\/([0-9a-f-]{36})$/)?.[1];
  // Only the Invitations page needs the whole list (up to 2,000 rows); every other page needs just the event.
  const onList = stage.path === "/admin" && !recordId;
  const load = async () => {
    try {
      if (onList) setData(await api("/backoffice/invitations"));
      else { const event = await api("/backoffice/event"); setData(d => ({ ...d, event })); }
    } catch { /* view-only may lack some calls */ }
    try { setSum(await api("/backoffice/summary")); setSumFailed(false); } catch { setSumFailed(true); /* keep the last numbers */ }
  };
  usePoll(load, 10000, true, onList);   // switching to or from Invitations reloads at once

  const n = (k) => sum?.[k] ?? "–";
  const unsent = sum?.unsent ?? 0;
  const badges = { "/admin": unsent ? `${unsent} unsent` : null, "/admin/codes": sum?.codesIssued ? `${sum.codesIssued}` : null, "/admin/human": sum?.needsHuman ? `${sum.needsHuman}` : null };
  // "Dec 11". A plain date ("2026-12-11") is that calendar day; a moment is shown in Eastern time.
  const day = (v) => {
    if (!v) return "not set";
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(v);
    return new Date(dateOnly ? `${v}T00:00:00Z` : v).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: dateOnly ? "UTC" : "America/Detroit" });
  };
  const note = !sum ? (sumFailed ? "Couldn't load these numbers. They try again every 10 seconds." : "Loading…")
    : `Registration closes ${day(sum.event?.registrationCloses)} · names lock ${day(sum.namesLock)}.`
      + (sum.event?.when === "dates to be confirmed" ? " The event's dates aren't set, so invitations say \u201cdates to be confirmed\u201d." : "");

  return (
    <div className="bo">
      <header className="bo-header">
        <a className="brand" href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }} aria-label="Lipari Food Show, home">
          <img src={wordmark} alt="Lipari" className="wordmark" /><span className="divider" aria-hidden="true" /><span className="sub">FOOD SHOW</span>
        </a>
        <nav className="bo-stages" aria-label="Back office">
          {STAGES.map(s => (
            <button key={s.path} className={`bo-stage ${stage.path === s.path ? "on" : ""} ${s.soon ? "soon" : ""}`} disabled={Boolean(s.soon)} title={s.soon ? `Arrives with the ${s.soon} work` : undefined} onClick={() => navigate(s.path)}>
              <span className="n">{s.n}</span><span>{s.label}</span>{badges[s.path] && <span className="badge">{badges[s.path]}</span>}{s.soon && <span className="soon-tag">{s.soon}</span>}
            </button>
          ))}
        </nav>
        <div className="who">{data.event?.name ? <span className="ev">{data.event.name}</span> : null}{who}{devBypass && DevSwitch && <DevSwitch />}{!devBypass && <button className="ghost signout" onClick={signOut}>Sign out</button>}</div>
      </header>

      <div className="bo-kpis">
        <StatStrip big items={[
          { k: "Registered", v: n("registered"), sub: `of ${n("invited")} invited`, color: "#2f6b3a" },
          { k: "People coming", v: n("people"), sub: `${n("peopleBooked")} booked` },
          { k: "Codes issued", v: n("codesIssued"), sub: `${unsent} invitation${unsent === 1 ? "" : "s"} not yet sent${sum?.codesCanceled ? ` · ${sum.codesCanceled} canceled` : ""}` },
          { k: "Booked by the agent", v: n("codesBooked"), sub: `of ${n("codesIssued")} codes${sum?.codesToRelease ? ` · ${sum.codesToRelease} to release` : ""}`, color: "#786018" },
        ]} />
        <div className="bo-kpinote">{note}</div>
      </div>

      <main className="bo-main">
        {recordId && <RecordPage id={recordId} />}
        {onList && <InvitationsPage data={data} reload={load} isAdmin={isAdmin} />}
        {stage.path === "/admin/event" && <EventPage event={data.event} reload={load} isAdmin={isAdmin} />}
        {stage.path === "/admin/access" && <AccessPage isAdmin={isAdmin} />}
        {stage.path === "/admin/codes" && <Agent canBook={isAdmin} embedded />}
        {stage.path === "/admin/human" && <HumanPage isAdmin={isAdmin} reload={load} />}
        {stage.path === "/admin/monitoring" && <MonitoringPage event={data.event} />}
      </main>
    </div>
  );
}
